import { useState, useCallback, useMemo, useEffect } from "react"
import { Trans, useTranslation } from "next-i18next"
import FormInput from "../../components/FormInput"
import Button from "../../components/Button"
import { GoogleLoginButton } from "react-social-login-buttons"
import { signInWithGoogle } from "../../utils/auth"
import { isMobile } from "../../utils/browser"
import Image from "next/image"
import Link from "next/link"

function SignUpSteps({ mode = "mind-r", step, setStep }) {
  const { t } = useTranslation("common")

  const [termsAndPrivacy, setTermsAndPrivacy] = useState(false)
  const [olderThan18, setOlderThan18] = useState(false)

  const stepLabels = [
    <>
      <div className="flex items-center">
        {t("SIGNUP_NEXT")}
        <Image
          width={24}
          height={24}
          className="ml-2"
          src="/assets/icon-arrow-right-white.svg"
          alt="arrow-right"
        />
      </div>
    </>,
    t("SIGNUP_CREATE_ACCOUNT"),
  ]

  const isMainButtonDisabled = useMemo(() => {
    if (step === 0 && mode === "mind-r") {
      return !termsAndPrivacy || !olderThan18
    }

    if (step === 0 && mode === "voxme") {
      return !termsAndPrivacy
    }

    return false
  }, [step, mode, termsAndPrivacy, olderThan18])

  const handleMainButton = useCallback(() => {
    if (isMainButtonDisabled) {
      return
    }

    if (step === 1) {
      return
    }

    setStep(step + 1)
  }, [setStep, isMainButtonDisabled, step])

  const handleBack = () => setStep(0)

  const handleTermsAndPrivacy = () => setTermsAndPrivacy(!termsAndPrivacy)
  const handleOlderThan18 = () => setOlderThan18(!olderThan18)

  useEffect(() => {
    if (step === 1 && isMobile()) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }
  }, [step])

  const stepFields = {
    0: (
      <>
        <FormInput
          label={
            <Trans
              i18nKey="SIGNUP_TERMS_AND_CONDITIONS_LABEL"
              components={{
                terms: (
                  <Link
                    href="/terms-and-conditions"
                    style={{ color: "rgb(0, 25, 255)" }}
                  />
                ),
                privacy: (
                  <Link
                    href="privacy-policy"
                    style={{ color: "rgb(0, 25, 255)" }}
                  />
                ),
              }}
            />
          }
          type="checkbox"
          value={termsAndPrivacy}
          onChange={handleTermsAndPrivacy}
        />
        {mode === "mind-r" && (
          <FormInput
            label={t("SIGNUP_OLDER_THAN_18_LABEL")}
            type="checkbox"
            value={olderThan18}
            onChange={handleOlderThan18}
          />
        )}
      </>
    ),
    1: (
      <div className="m-0 p-0 mb-10 font-sans max-w-sm tracking-wider">
        <div className="my-5">
          <GoogleLoginButton
            style={{
              border: "2px solid #A8B9BF",
              borderRadius: "20px",
              boxShadow: "none",
              lineHeight: "1.2rem",
            }}
            onClick={signInWithGoogle}
          >
            {t("SIGNUP_GOOGLE_BUTTON")}
          </GoogleLoginButton>
        </div>
      </div>
    ),
  }

  return (
    <>
      <div className="m-0 p-0 mb-10 font-sans max-w-sm tracking-wider">
        <Trans
          i18nKey="SIGNUP_ALREADY_HAVE_ACCOUNT"
          components={{
            signin: (
              <Link
                href={`/sign-in${mode === "voxme" ? "-voxme" : ""}`}
                className="font-bold"
                style={{ color: "rgb(0, 25, 255)" }}
              />
            ),
          }}
        />
      </div>
      {stepFields[step]}
      <div className="flex justify-between w-full h-20 relative">
        {step === 1 && (
          <Button onClick={handleBack} style="secondary">
            <div className="flex items-center">
              <Image
                width={24}
                height={24}
                className="mr-2"
                src="/assets/icon-arrow-left-black.svg"
                alt="arrow-right"
              />

              {t("SIGNUP_BACK")}
            </div>
          </Button>
        )}

        {step === 0 && (
          <Button
            onClick={handleMainButton}
            className={`absolute  right-0    text-white text-lg sm:text-2xl font-sans    ${
              isMainButtonDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={isMainButtonDisabled}
          >
            {stepLabels[step]}
          </Button>
        )}
      </div>
    </>
  )
}

export default SignUpSteps
